<script setup>
import { format } from 'date-fns';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  event: {
    type: Object,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const formatDate = dateString => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return format(date, 'HH:mm:ss, dd/MM/yyyy');
};

const formatTimeThreshold = seconds => {
  if (seconds === null || seconds === undefined) return 'N/A';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const parts = [];
  if (hours >= 1) parts.push(`${hours}h`);
  if (minutes >= 1) parts.push(`${minutes}min`);

  return parts.length > 0 ? parts.join(' ') : `${Math.floor(seconds)}s`;
};

const formatFieldName = field => {
  const fieldMap = {
    name: 'Nome',
    description: 'Descrição',
    first_response_time_threshold: 'Tempo de Primeira Resposta',
    next_response_time_threshold: 'Tempo de Próxima Resposta',
    resolution_time_threshold: 'Tempo de Resolução',
    only_during_business_hours: 'Apenas Horário Comercial',
  };
  return fieldMap[field] || field;
};

const formatFieldValue = (field, value) => {
  if (value === null || value === undefined) return 'N/A';

  if (field.includes('threshold')) {
    return formatTimeThreshold(value);
  }
  if (field === 'only_during_business_hours') {
    return value ? 'Sim' : 'Não';
  }
  return String(value);
};

const getChanges = () => {
  const meta = props.event.meta || {};
  const changes = meta.changes || {};

  return Object.entries(changes).map(([field, [oldVal, newVal]]) => ({
    field,
    fieldName: formatFieldName(field),
    oldValue: formatFieldValue(field, oldVal),
    newValue: formatFieldValue(field, newVal),
  }));
};

const changes = getChanges();
const updatedBy = props.event.meta?.updated_by_user_name || 'N/A';
const updatedAt =
  props.event.meta?.updated_at || props.event.created_at || 'N/A';
const slaPolicyName = props.event.meta?.sla_policy_name || 'N/A';
</script>

<template>
  <div
    class="bg-n-solid-2 rounded-lg border-l-4 border-blue-500"
    :class="compact ? 'p-2 pr-8' : 'p-4'"
  >
    <div class="mb-1 text-xs">
      <div class="font-medium text-n-slate-12 mb-0.5">
        {{ slaPolicyName }} {{ t('TICKETS.SLA.UPDATED')
        }}{{ t('TICKETS.COLON') }}
      </div>
      <div class="text-n-slate-11">
        {{ formatDate(updatedAt) }}
      </div>
    </div>

    <div v-if="changes.length > 0" class="space-y-1.5">
      <div v-for="change in changes" :key="change.field" class="text-xs">
        <div class="font-medium text-n-slate-12 mb-0.5">
          {{ change.fieldName }}{{ t('TICKETS.COLON') }}
        </div>
        <div class="flex items-center gap-1">
          <span class="text-n-slate-11 line-through">
            {{ change.oldValue }}
          </span>
          <span class="text-n-slate-10">{{ t('TICKETS.ARROW') }}</span>
          <span class="text-n-slate-12 font-medium">
            {{ change.newValue }}
          </span>
        </div>
      </div>
    </div>

    <div v-else class="text-xs text-n-slate-11">
      {{ t('TICKETS.SLA.NO_CHANGES_DETECTED') }}
    </div>

    <div v-if="updatedBy" class="mt-1 text-xs">
      <div class="font-medium text-n-slate-12">
        {{ t('TICKETS.SLA.UPDATED_BY') }}{{ t('TICKETS.COLON') }}
      </div>
      <div class="text-n-slate-11">
        {{ updatedBy }}
      </div>
    </div>
  </div>
</template>
